import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";

export default function DripCalendar({ embedded }) {
  const [data, setData] = useState(null);
  useEffect(() => { client.get("/admin/drip-calendar").then((r) => setData(r.data)).catch(() => {}); }, []);
  const items = data?.items || [];
  const editionKey = (iso) => (iso ? cycleIndexFor(String(iso).slice(0, 10)) : null);

  const live = items.filter((i) => i.status === "published");
  const scheduled = items.filter((i) => (i.status === "scheduled" || i.status === "planned") && i.publish_at);
  const drafts = items.filter((i) => i.status === "draft" || (i.status === "scheduled" && !i.publish_at));
  const editions = [...new Set(scheduled.map((i) => editionKey(i.publish_at)))].sort((a, b) => a - b);

  const Row = ({ i }) => (
    <div className="flex items-center gap-3 py-3 border-b border-ink/10 last:border-0" data-testid={`cal-item-${i.id}`}>
      <span className="overline text-gloss-silver w-32 shrink-0 truncate">{i.label}</span>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-base truncate">{i.title || "Untitled"}</p>
        <p className="font-sans text-xs text-ink/50 truncate">{i.business_name}</p>
      </div>
      {i.publish_at && (i.status === "scheduled" || i.status === "planned") && <span className="overline text-ink/45 shrink-0">{String(i.publish_at).slice(0, 10)}</span>}
      {i.status === "planned"
        ? <span className="overline bg-gloss-gold/25 text-ink/70 px-2 py-1 shrink-0">Planned</span>
        : <Badge status={i.status} />}
      {i.slug && <a href={`${window.location.origin}/read/${i.slug}`} target="_blank" rel="noreferrer" className="p-2 border border-ink/25 hover:border-ink transition-colors shrink-0" title="Preview" data-testid={`cal-preview-${i.id}`}><Eye size={14} /></a>}
    </div>
  );
  const Section = ({ title, note, rows, testid }) => (
    <div className="mb-8" data-testid={testid}>
      <div className="mb-2">
        <p className="overline text-ink/70">{title}</p>
        {note && <p className="font-sans text-xs text-ink/45">{note}</p>}
      </div>
      <div className="bg-white border border-ink/15 px-5 sm:px-6">
        {rows.length ? rows.map((i) => <Row key={i.id} i={i} />) : <p className="font-sans text-sm text-ink/40 py-4">Nothing here yet.</p>}
      </div>
    </div>
  );

  return (
    <div data-testid="drip-calendar">
      {!embedded && (
        <Header kicker="Plan the editions" title="Drip Calendar">
          {data && <p className="overline text-ink/50">Issue Nº {String(data.current_issue_no).padStart(2, "0")} · 2 editions a month · next: {data.next_editions?.[0]?.starts}</p>}
        </Header>
      )}
      <div className={embedded ? "py-8" : "px-6 sm:px-10 py-8"}>
        {embedded && data && <p className="overline text-ink/50 mb-4">Issue Nº {String(data.current_issue_no).padStart(2, "0")} · 2 editions a month · next: {data.next_editions?.[0]?.starts}</p>}
        <p className="font-sans text-sm text-ink/55 mb-4 max-w-2xl">Every member piece across the whole magazine, planned by edition. See what is live now, what drips out in which edition, and which drafts still need a date. To change a piece, open its member in <b>Members</b>.</p>
        {data?.next_editions && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="edition-planner">
            {data.next_editions.map((e) => (
              <span key={e.starts} className="overline border border-ink/25 px-3 py-1.5 text-ink/60">Nº {String(e.issue_no).padStart(2, "0")} · {e.starts}</span>
            ))}
          </div>
        )}
        <Section title="Live now" note="Published and visible to readers." rows={live} testid="cal-live" />
        {editions.map((idx) => (
          <Section key={idx} title={editionLabel(idx)} note="Scheduled to drip out in this edition." rows={scheduled.filter((i) => editionKey(i.publish_at) === idx)} testid={`cal-edition-${idx + 1}`} />
        ))}
        <Section title="Unscheduled drafts" note="Written but not yet given a drip date — set one in the member's hub." rows={drafts} testid="cal-drafts" />
      </div>
    </div>
  );
}


/* ---------------- Members ---------------- */
